import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Stack, Button, Group, SimpleGrid, Text } from '@repletoryreach/sdk-ui';
import {
  Alert,
  EmptyState,
  FilterDrawer,
  PageHeader,
  TableShell,
  type FilterField,
  type FilterValues,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { downloadText, rowsToCsv } from '@repletoryreach/sdk-utils/file';
import { useLedgerPages } from './useLedgerPages';
import { useTenantMap } from '../../../data/useTenants';
import { LedgerTable } from './components/LedgerTable';
import { LedgerList } from './components/LedgerList';
import { StatCard } from '../../../components/StatCard';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { TableFooter } from '../../../components/TableFooter';
import { BillingToolbar } from '../components/BillingToolbar';
import { ViewToggle } from '../components/ViewToggle';
import { entryTypeOf } from '../components/cells';
import { IconAdjustment, IconCredit, IconDebit, IconDownload, IconLedger, IconTag } from '../components/icons';
import { withinRange, type DateRangeKey } from '../components/dateRange';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatInteger, formatMinor } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import '../components/billing.css';

/**
 * How many entries one fetch brings back. The endpoint clamps to 1…1000 and
 * pages with a keyset cursor, so this is the size of a page rather than a
 * ceiling on the journal: Load more walks the rest of the window.
 */
const LIMITS = [100, 250, 500, 1000];
const DEFAULT_LIMIT = 500;

const GRID_COLS = { base: 1, sm: 2, lg: 5 };

type SortValue = 'occurred_at:desc' | 'occurred_at:asc' | 'amount_minor:desc' | 'amount_minor:asc';

export default function LedgerPage() {
  const t = useT();
  const [params] = useSearchParams();
  const initialOrg = params.get('org_id') ?? '';

  const [filters, setFilters] = useState<FilterValues>(initialOrg ? { org_id: initialOrg } : {});
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const orgId = filters.org_id ?? '';

  const {
    rows: all,
    total: windowTotal,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch
  } = useLedgerPages({ org_id: orgId || undefined, limit });
  const { map: tenants } = useTenantMap();

  const [view, setView] = usePersistedState<ViewMode>('admin.ledger.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.ledger.pageSize', 25);

  const [query, setQuery] = useState('');
  const [range, setRange] = useState<DateRangeKey>('all');
  const [sort, setSort] = useState<SortValue>('occurred_at:desc');
  const [page, setPage] = useState(1);

  /**
   * The window the stat cards describe. They are a breakdown of what is on
   * screen, so they follow the date range and the search but not the entry-type
   * filter: a card that counted only what its own filter admits would always
   * read the same number as the table.
   */
  const inWindow = useMemo(() => {
    const now = Date.now();
    let rows = all.filter((e) => withinRange(e.occurred_at, range, now));
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (e) =>
          (e.description ?? '').toLowerCase().includes(q) ||
          (e.sku_code ?? '').toLowerCase().includes(q) ||
          (e.idempotency_key ?? '').toLowerCase().includes(q) ||
          (tenants.get(e.organisation_id)?.name ?? '').toLowerCase().includes(q) ||
          (tenants.get(e.organisation_id)?.slug ?? '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [all, range, query, tenants]);

  /**
   * Totals are summed per currency, because adding cents of one currency to
   * cents of another produces a number that means nothing. Every figure is a
   * minor-unit integer until `formatMinor` divides it exactly once.
   */
  function totalsOf(rows: typeof all): string {
    const byCurrency = new Map<string, number>();
    for (const e of rows) byCurrency.set(e.currency, (byCurrency.get(e.currency) ?? 0) + e.amount_minor);
    if (byCurrency.size === 0) return '';
    return Array.from(byCurrency.entries())
      .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
      .map(([currency, minor]) => formatMinor(minor, currency))
      .join(' · ');
  }

  const breakdown = useMemo(() => {
    const credits = inWindow.filter((e) => entryTypeOf(e) === 'credit');
    const debits = inWindow.filter((e) => entryTypeOf(e) === 'debit');
    const adjustments = inWindow.filter((e) => entryTypeOf(e) === 'adjustment');
    const skus = new Set(inWindow.map((e) => e.sku_code).filter(Boolean) as string[]);
    return { credits, debits, adjustments, skus };
    // `totalsOf` is a pure function of its argument, so it is safe to leave out.
  }, [inWindow]);

  function hint(rows: typeof all) {
    const total = totalsOf(rows);
    return total ? t('billing.ledger.stat.totalHint', { total }) : t('billing.ledger.stat.noAmounts');
  }

  const filterFields: FilterField[] = useMemo(() => {
    const currencies = Array.from(new Set(all.map((e) => e.currency))).sort();
    const skus = Array.from(new Set(all.map((e) => e.sku_code).filter(Boolean) as string[])).sort();
    // Organisations come from the tenants list rather than the loaded page: the
    // org filter is applied by the server, so an org absent from the newest 500
    // rows is exactly the one an operator needs to be able to choose.
    const orgs = Array.from(tenants.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((o) => ({ value: o.id, label: o.name }));
    return [
      { key: 'org_id', label: t('billing.ledger.filters.organisation'), placeholder: t('billing.subscriptions.filters.anyOrg'), options: orgs },
      {
        key: 'entry_type',
        label: t('billing.ledger.filters.entryType'),
        placeholder: t('billing.ledger.filters.anyType'),
        options: [
          { value: 'credit', label: t('billing.ledger.type.credit') },
          { value: 'debit', label: t('billing.ledger.type.debit') },
          { value: 'adjustment', label: t('billing.ledger.type.adjustment') }
        ]
      },
      { key: 'currency', label: t('common.currency'), placeholder: t('billing.ledger.filters.anyCurrency'), options: currencies.map((c) => ({ value: c, label: c })) },
      { key: 'sku_code', label: t('billing.ledger.filters.skuCode'), placeholder: t('billing.ledger.filters.anySku'), options: skus.map((s) => ({ value: s, label: s })) }
    ];
  }, [all, tenants, t]);

  const hasActiveFilters =
    Boolean(query.trim()) || range !== 'all' || Object.values(filters).some((v) => v && v !== 'all');

  function clearAllFilters() {
    setQuery(''); setRange('all'); setFilters({}); setPage(1);
  }

  const typeLabels: Record<string, string> = {
    credit: t('billing.ledger.type.credit'),
    debit: t('billing.ledger.type.debit'),
    adjustment: t('billing.ledger.type.adjustment')
  };
  const rangeLabels: Record<DateRangeKey, string> = {
    all: t('billing.range.all'), today: t('billing.range.today'), '7d': t('billing.range.last7'), '30d': t('billing.range.last30')
  };

  /** One removable pill per narrowing choice, so a short list is never a mystery. */
  const pills: ActiveFilterPill[] = [];
  if (range !== 'all') pills.push({ id: 'range', field: t('billing.range.label'), value: rangeLabels[range] });
  if (filters.org_id) pills.push({ id: 'org_id', field: t('billing.ledger.filters.organisation'), value: tenants.get(filters.org_id)?.name ?? t('tenantName.unknownShort') });
  if (filters.entry_type) pills.push({ id: 'entry_type', field: t('billing.ledger.filters.entryType'), value: typeLabels[filters.entry_type] ?? filters.entry_type });
  if (filters.currency) pills.push({ id: 'currency', field: t('common.currency'), value: filters.currency });
  if (filters.sku_code) pills.push({ id: 'sku_code', field: t('billing.ledger.filters.skuCode'), value: filters.sku_code });

  function removePill(id: string) {
    setPage(1);
    if (id === 'range') { setRange('all'); return; }
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  const filtered = useMemo(() => {
    let rows = inWindow;
    if (filters.entry_type) rows = rows.filter((e) => entryTypeOf(e) === filters.entry_type);
    if (filters.currency) rows = rows.filter((e) => e.currency === filters.currency);
    if (filters.sku_code) rows = rows.filter((e) => e.sku_code === filters.sku_code);
    const [key, direction] = sort.split(':') as ['occurred_at' | 'amount_minor', 'asc' | 'desc'];
    const dir = direction === 'asc' ? 1 : -1;
    // Sorting happens over the whole filtered set, not the visible page, so
    // "largest debit" means largest in the window rather than on page four.
    return [...rows].sort((a, b) => (a[key] < b[key] ? -1 * dir : a[key] > b[key] ? dir : 0));
  }, [inWindow, filters, sort]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  /**
   * The export names its columns and carries the organisation's name and a
   * formatted amount, because the file is opened by a person in a spreadsheet.
   * `amount_minor` stays alongside so the exact stored integer is never lost.
   */
  function exportCsv() {
    const rows = filtered.map((e) => ({
      occurred_at: e.occurred_at,
      organisation: tenants.get(e.organisation_id)?.name ?? '',
      organisation_id: e.organisation_id,
      entry_type: entryTypeOf(e),
      sku_code: e.sku_code ?? '',
      amount: formatMinor(e.amount_minor, e.currency),
      amount_minor: e.amount_minor,
      currency: e.currency,
      description: e.description ?? '',
      reference: e.idempotency_key ?? '',
      entry_id: e.id
    }));
    const headers = [
      'occurred_at', 'organisation', 'organisation_id', 'entry_type', 'sku_code',
      'amount', 'amount_minor', 'currency', 'description', 'reference', 'entry_id'
    ];
    downloadText(rowsToCsv(rows, { headers, bom: true }), `ledger-${Date.now()}.csv`, 'text/csv');
    notifySuccess(
      t('billing.ledger.toast.exported', { count: rows.length }),
      t('billing.ledger.toast.exportedTitle')
    );
  }

  /**
   * More pages sit behind this one. It drives the Load more button and keeps
   * the footer honest: "1 to 25 of 500" whilst four thousand entries match the
   * window is exactly the quiet lie `capped` exists to avoid.
   */
  const hasMore = Boolean(hasNextPage);
  const countLine = hasMore
    ? t('billing.ledger.loadedOfWindow', {
        shown: formatInteger(filtered.length),
        loaded: formatInteger(all.length),
        total: formatInteger(windowTotal)
      })
    : t('billing.ledger.showingCount', { shown: formatInteger(filtered.length), total: formatInteger(all.length) });

  const totalLabel = (
    <Group gap="xs" align="center" wrap="nowrap">
      <span>{countLine}</span>
      {hasMore && (
        <Tooltip label={t('billing.ledger.morePagesHelp')} position="top" openDelay={150}>
          <span style={{ display: 'inline-flex', color: 'var(--rr-color-text-muted)' }} aria-label={t('billing.ledger.morePagesHelp')} role="img">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
              <circle cx="8" cy="8" r="6.3" />
              <path d="M8 7.2v4M8 4.9h.01" strokeLinecap="round" />
            </svg>
          </span>
        </Tooltip>
      )}
    </Group>
  );

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.ledger.eyebrow')}
        title={t('billing.ledger.title')}
        description={t('billing.ledger.description')}
        actions={
          <Group gap="sm" wrap="nowrap">
            <Button size="sm" variant="outline" leftSection={<IconDownload />} disabled={filtered.length === 0} onClick={exportCsv}>
              {t('action.exportCsv')}
            </Button>
            <ViewToggle value={view} onChange={setView} />
          </Group>
        }
      />

      <SimpleGrid cols={GRID_COLS} spacing="md">
        <StatCard
          withRule tone="primary" icon={<IconLedger />}
          label={t('billing.ledger.stat.all')}
          value={formatInteger(inWindow.length)}
          hint={hint(inWindow)}
          loading={isLoading}
        />
        <StatCard
          withRule tone="success" icon={<IconCredit />}
          label={t('billing.ledger.stat.credits')}
          value={formatInteger(breakdown.credits.length)}
          hint={hint(breakdown.credits)}
          loading={isLoading}
        />
        <StatCard
          withRule tone="error" icon={<IconDebit />}
          label={t('billing.ledger.stat.debits')}
          value={formatInteger(breakdown.debits.length)}
          hint={hint(breakdown.debits)}
          loading={isLoading}
        />
        <StatCard
          withRule tone="warning" icon={<IconAdjustment />}
          label={t('billing.ledger.stat.adjustments')}
          value={formatInteger(breakdown.adjustments.length)}
          hint={hint(breakdown.adjustments)}
          loading={isLoading}
        />
        <StatCard
          withRule tone="info" icon={<IconTag />}
          label={t('billing.ledger.stat.skus')}
          value={formatInteger(breakdown.skus.size)}
          hint={t('billing.ledger.stat.activeSkus')}
          loading={isLoading}
        />
      </SimpleGrid>

      <BillingToolbar
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('billing.ledger.search')}
        range={range}
        onRangeChange={(next) => { setRange(next); setPage(1); }}
      >
        <Select
          size="xs"
          aria-label={t('billing.ledger.filters.pageSize')}
          value={String(limit)}
          onChange={(e) => { setLimit(Number(e.currentTarget.value)); setPage(1); }}
          data={LIMITS.map((n) => ({ value: String(n), label: t('billing.ledger.filters.pageRows', { count: n }) }))}
        />
        <FilterDrawer
          fields={filterFields}
          values={filters}
          onApply={(next) => { setFilters(next); setPage(1); }}
          onClear={clearAllFilters}
        />
      </BillingToolbar>

      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <TableShell
        totalLabel={totalLabel}
        actions={
          <Group gap="xs" align="center" wrap="nowrap">
            <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.ledger.sortedBy')}</Text>
            <Select
              size="xs"
              aria-label={t('billing.ledger.sortedBy')}
              value={sort}
              onChange={(e) => { setSort(e.currentTarget.value as SortValue); setPage(1); }}
              data={[
                { value: 'occurred_at:desc', label: t('billing.ledger.sort.newest') },
                { value: 'occurred_at:asc', label: t('billing.ledger.sort.oldest') },
                { value: 'amount_minor:desc', label: t('billing.ledger.sort.amountHigh') },
                { value: 'amount_minor:asc', label: t('billing.ledger.sort.amountLow') }
              ]}
            />
          </Group>
        }
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <EmptyState
            title={t('billing.ledger.empty')}
            description={hasActiveFilters ? t('common.tryClearingFilters') : t('billing.ledger.emptyHint')}
            action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
        // Only when nothing loaded at all. A page that fails part way through
        // still has rows worth reading, and replacing them with an error box
        // would throw away everything the operator had already fetched: that
        // failure is reported beside the Load more button instead.
        error={
          error && all.length === 0 ? (
            <Alert color="error">
              <Stack gap="sm">
                <Text role="bodySmall">{t('billing.ledger.loadError')}</Text>
                <Group>
                  <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
                </Group>
              </Stack>
            </Alert>
          ) : null
        }
        pagination={
          <Stack gap="sm">
            <TableFooter
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              capped={hasMore}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            />
            {(hasMore || (error && all.length > 0)) && (
              <Group justify="center" gap="sm" align="center" wrap="wrap">
                {error && all.length > 0 && (
                  <Text role="caption" c="var(--rr-color-error)">{t('billing.ledger.loadMoreError')}</Text>
                )}
                {hasMore && (
                  <Button
                    size="xs"
                    variant="outline"
                    loading={isFetchingNextPage}
                    onClick={() => void fetchNextPage()}
                  >
                    {t('billing.ledger.loadMore')}
                  </Button>
                )}
              </Group>
            )}
          </Stack>
        }
      >
        {view === 'table' && (
          <LedgerTable
            rows={paged as never}
            loading={isLoading}
            sort={sort}
            onSortChange={(next) => { setSort(next); setPage(1); }}
          />
        )}
        {view === 'list' && <LedgerList records={paged} />}
      </TableShell>
    </Stack>
  );
}
